import isEmpty from 'lodash/isEmpty';
import { createSelector } from 'reselect';

import { selectEventReasonPromptSnoozePreference } from 'store/modules/myPreferences/selectors';
import { ApplicationRootState } from 'store/types';
import { TriggerType } from 'types/trigger.enum';

const selectEventReasonsState = (state: ApplicationRootState) => state.eventReasons;

const selectEventReasonCatalog = createSelector(
  selectEventReasonsState,
  (eventReasonsState) => eventReasonsState.catalogByTriggerType
);

const selectIsEventReasonCatalogLoaded = createSelector(
  selectEventReasonCatalog,
  (catalogByTriggerType) => !isEmpty(catalogByTriggerType)
);

const selectEventReasonsForTriggerType = createSelector(
  selectEventReasonCatalog,
  (_state: ApplicationRootState, triggerType: TriggerType) => triggerType,
  (catalogByTriggerType, triggerType) => catalogByTriggerType[triggerType] ?? []
);

const selectEventReasonPromptContext = createSelector(
  selectEventReasonsState,
  (eventReasonsState) => eventReasonsState.promptContext
);

const selectPendingEventReasonTaggings = createSelector(
  selectEventReasonsState,
  (eventReasonsState) => eventReasonsState.pendingTaggings
);

const selectPendingEventReasonTaggingsAsArray = createSelector(
  selectPendingEventReasonTaggings,
  (pendingTaggings) => Object.values(pendingTaggings)
);

const selectPendingReasonPromptSnooze = createSelector(
  selectEventReasonsState,
  (eventReasonsState) => eventReasonsState.pendingSnooze
);

/**
 * The effective selector is necessary because:
 * myPreferences is not persisted. pendingSnooze is. After a refresh (or process kill) while still offline:
 * - pendingSnooze comes back from redux-persist
 * - myPreferences is empty until fetchMyPreferences succeeds.
 * Without the merge, the prompt would reopen after refresh even though the user already snoozed.
 */
const selectEffectiveReasonPromptSnooze = createSelector(
  selectEventReasonPromptSnoozePreference,
  selectPendingReasonPromptSnooze,
  (serverPreference, pendingSnooze) => {
    const snoozedUntil = Math.max(
      serverPreference?.snoozedUntil ?? 0,
      pendingSnooze?.snoozedUntil ?? 0
    );
    return snoozedUntil > 0 ? { snoozedUntil } : undefined;
  }
);

export {
  selectEffectiveReasonPromptSnooze,
  selectEventReasonPromptContext,
  selectEventReasonsForTriggerType,
  selectIsEventReasonCatalogLoaded,
  selectPendingEventReasonTaggingsAsArray,
  selectPendingReasonPromptSnooze
};
